import { MagicView } from '@upright/ui/views/internal/magic-view';
import { lazy, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { renderViewUiPath } from '../../../../../../common/renderer/renderer-settings';
import { RouteDatum } from '../types';
import { SSO_ERROR_PAGE_PATH } from '@upright/common/iam/login';
import { EXTERNAL_API_DOCS_UI_PATH } from '@upright/common/external-api-docs';
import { ARVO_DOCS_UI_PATH, LEGACY_ARVO_DOCS_UI_PATH, movedFromLegacyDocsPath } from '@upright/common/arvo/docs/paths';
import { movedEevoOrigin } from '@upright/common/arvo/public-url';
import { BouAppLoading } from '@upright/ui/common/generic/components/progress/spinners/bou-app-loading';
import { SsoErrorView } from '@upright/ui/views/login-views/login-view/sso-error-view';
import { withChatFrame } from '@upright/ui/views/internal/with-empty-frame';
import { CLIMATE_TRIAL_PATH } from '@upright/ui/views/climate-trial-view/resolve-climate-trial-destination';
import { SUPPLIER_TRIAL_PATH } from '@upright/ui/views/supplier-trial-view/resolve-supplier-trial-destination';
import { ALL_CHATS_PATH, CHAT_PATH } from '@upright/ui/views/unified-shell/unified-shell-chat-routes';
import { useUnifiedShellFrameActive } from '@upright/ui/views/unified-shell/use-unified-shell-frame-active';
import { SCREENER_BASE_PATH } from '@upright/ui/common/generic/components/navigation/page-paths';

const RenderView = lazy(async () => await import('@upright/ui/views/render-view'));
const ApiDocsView = lazy(async () => await import('@upright/ui/views/api-docs'));
const ArvoDocsView = lazy(async () => await import('@upright/ui/views/arvo-docs'));
const BlanketView = lazy(async () => await import('@upright/ui/views/blanket'));
const FinalizeUserView = lazy(async () => await import('@upright/ui/views/login-views/finalize-user-view'));
const ResetPasswordView = lazy(async () => await import('@upright/ui/views/login-views/reset-password-view'));
const SignInWithCodeView = lazy(async () => await import('@upright/ui/views/login-views/sign-in-with-code-view'));
const VerificationView = lazy(async () => await import('@upright/ui/common/upright/iam/verification-view'));
const UserView = lazy(async () => await import('@upright/ui/views/user-view'));

const AdvancedSearchView = lazy(async () => await import('@upright/ui/views/advanced-search-view'));
const McpAuthorizeView = lazy(async () => await import('@upright/ui/views/mcp-authorize'));
const McpInternalAuthorizeView = lazy(async () => await import('@upright/ui/views/internal/mcp-internal-authorize'));
const GuestInvitationView = lazy(async () => await import('@upright/ui/views/guest-invitation-view'));
const SelfSignupOwnPortfolios = lazy(
  async () => await import('@upright/ui/views/home-views/rebecca-own-portfolios-view'),
);

// AI Chat lives under `/chat` (was `/internal/chat` until access was opened
// to non-team users gated by the `USE_AI_CHAT_WEB` privilege). The view itself
// applies the privilege gate; we just need to hand it a top-level route
// here.
const ChatView = lazy(async () => await import('@upright/ui/views/internal/chat-view'));
const ChatViewWithChatFrame = withChatFrame(ChatView);
const InvestorChatsView = lazy(async () => await import('@upright/ui/views/unified-shell/investor-chats-view'));

// `/go/…` holds click-time resolvers for outbound campaign links: one stable
// URL that decides where the reader actually belongs once we know who they are.
// They render a spinner and immediately redirect, so they get no frame.
const ClimateTrialView = lazy(async () => await import('@upright/ui/views/climate-trial-view'));
const SupplierTrialView = lazy(async () => await import('@upright/ui/views/supplier-trial-view'));

/**
 * Where the Eevo docs answer on a platform host. Production and staging, whose Eevo has a host of its
 * own (PLT-7477), claim only the old `/docs/arvo/*` links and send them on to the same page there:
 * the rest of `/docs/*` stays the platform's (`/docs/api` and whatever comes next). Development
 * environments and local have no Eevo host, so the docs render here at `/docs/*`, where their own
 * links point; `/docs/api` is the more specific route, so it stays the API reference.
 */
const PLATFORM_ARVO_DOCS_ROUTE = movedEevoOrigin(window.location.hostname)
  ? `${LEGACY_ARVO_DOCS_UI_PATH}/*`
  : `${ARVO_DOCS_UI_PATH}/*`;

export const otherRoutes: RouteDatum[] = [
  { path: renderViewUiPath, element: <RenderView /> },
  // The external API reference; public once launched, see `isPlatformApiDocsLaunched`.
  { path: EXTERNAL_API_DOCS_UI_PATH, element: <ApiDocsView /> },
  // The Eevo docs on a platform host; see `PLATFORM_ARVO_DOCS_ROUTE`.
  { path: PLATFORM_ARVO_DOCS_ROUTE, element: <PlatformArvoDocs /> },
  { path: '/profile/:username', element: <VerificationView /> },
  { path: '/user/:username', element: <UserView />, optionalParams: ['viewMode'] },
  { path: '/finalize/:username', element: <FinalizeUserView /> },
  { path: '/reset-password', optionalParams: ['code'], element: <ResetPasswordView /> },
  { path: '/sign-in', optionalParams: ['code'], element: <SignInWithCodeView /> },
  { path: '/blanket/*', element: <BlanketView /> },
  { path: `${SCREENER_BASE_PATH}/*`, element: <AdvancedSearchView /> },
  { path: '/magic', element: <MagicView /> },
  { path: SSO_ERROR_PAGE_PATH, element: <SsoErrorView /> },
  { path: '/my-portfolios', element: <SelfSignupOwnPortfolios /> },
  { path: '/mcp-authorize', element: <McpAuthorizeView /> },
  { path: '/mcp-internal-authorize', element: <McpInternalAuthorizeView /> },
  // The literal, not GUEST_INVITATION_PATH: importing it would pull the lazy view into this bundle.
  { path: '/guest-invitations/:invitationId', element: <GuestInvitationView /> },
  { path: CHAT_PATH, optionalParams: ['conversationId'], element: <ChatRoute /> },
  { path: ALL_CHATS_PATH, element: <AllChatsRoute /> },
  { path: CLIMATE_TRIAL_PATH, element: <ClimateTrialView /> },
  { path: SUPPLIER_TRIAL_PATH, element: <SupplierTrialView /> },
];

/**
 * The Eevo docs on a platform host (`PLATFORM_ARVO_DOCS_ROUTE`): where the console has moved to
 * Eevo's own host (PLT-7477), the same docs page there, else the docs themselves (development
 * environments).
 */
function PlatformArvoDocs() {
  const { pathname, hash } = useLocation();
  const eevoOrigin = movedEevoOrigin(window.location.hostname);
  useEffect(() => {
    if (eevoOrigin) window.location.replace(`${eevoOrigin}${movedFromLegacyDocsPath(pathname) ?? pathname}${hash}`);
  }, [eevoOrigin, pathname, hash]);
  return eevoOrigin ? <BouAppLoading /> : <ArvoDocsView />;
}

/**
 * Chat in whichever shell the user is in (PLT-6610).
 *
 * Inside the unified shell it is a view like any other: the route tree has already put the
 * shell's frame up around this outlet, so the chat mounts bare and the top bar and rail stay
 * where they were — which is what makes the rail's Chats group a list you navigate by, and a new
 * chat something that happens in place rather than in a shell that is built again. Everyone else
 * keeps the full-screen chat frame, with its own history sidebar, until the rollout retires it
 * (PLT-6596).
 *
 * `useUnifiedShellFrameActive` is the same hook the route tree and the header switch read, so the
 * three cannot disagree about which shell this route is in. Its `loading` state needs no branch
 * here: the frame holds the page with a spinner while a customer's opt-in resolves, so this
 * element is not mounted until the answer is known.
 */
function ChatRoute() {
  const frame = useUnifiedShellFrameActive();
  return frame.active ? <ChatView inUnifiedShell /> : <ChatViewWithChatFrame />;
}

/**
 * The full conversation list, which exists only inside the unified shell — it is the rail's
 * `All chats` destination, and the rail is what makes it reachable. Outside the shell the same
 * list is the `/chat` sidebar's, so there is nothing here to send anyone to.
 */
function AllChatsRoute() {
  const frame = useUnifiedShellFrameActive();
  return frame.active ? <InvestorChatsView /> : <Navigate to={CHAT_PATH} replace />;
}
